import type { InsurancePolicyModel } from "@/api/cle-api";
import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import DocumentIcon from "@/components/icons/DocumentIcon";
import { TableBody, TableHead, TableRoot } from "@/components/Table/TableBasic";

import { DocumentPaginationControls } from "./DocumentPaginationControls";
import { StatusBadge } from "./StatusBadge";
import {
  formatEvidenceStatus,
  getEvidenceStatusVariant,
} from "./utils/evidenceStatus";
import { formatAmount, formatDate } from "./utils/formatters";
import type { MockPagination } from "./types";

interface InsurancePoliciesTableProps {
  policies: InsurancePolicyModel[];
  isLoading: boolean;
  error: Error | null;
  onSelectInsurancePolicy: (insurancePolicyId: string) => void;
  pagination: MockPagination;
  showPagination?: boolean;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
}

export const InsurancePoliciesTable = ({
  policies,
  isLoading,
  error,
  onSelectInsurancePolicy,
  pagination,
  showPagination = true,
  onPreviousPage,
  onNextPage,
}: InsurancePoliciesTableProps) => (
  <>
    <TableRoot>
      <TableHead>
        <th>Dokument</th>
        <th>Forsikringsselskab</th>
        <th>Startdato</th>
        <th>Advokatansvar</th>
        <th>Garantibeløb</th>
        <th>Status</th>
        <th>Handling</th>
      </TableHead>
      <TableBody>
        {isLoading && (
          <tr>
            <td colSpan={7} className="text-muted">
              Henter forsikringspolicer ...
            </td>
          </tr>
        )}
        {error && (
          <tr>
            <td colSpan={7}>
              <CleApiErrorText
                error={error}
                fallback="Kunne ikke hente forsikringspolicer."
              />
            </td>
          </tr>
        )}
        {!isLoading &&
          !error &&
          policies.map((policy) => (
            <tr
              key={policy.id}
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => onSelectInsurancePolicy(policy.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectInsurancePolicy(policy.id);
                }
              }}
            >
              <td>
                <div className="d-flex align-items-center gap-2">
                  <DocumentIcon height={16} />
                  {policy.fileName ?? "-"}
                </div>
              </td>
              <td>{policy.insuranceCompany ?? "-"}</td>
              <td>{formatDate(policy.policyStartDate)}</td>
              <td>{formatAmount(policy.lawyerLiabilityAmount)}</td>
              <td>{formatAmount(policy.guaranteeAmount)}</td>
              <td>
                <StatusBadge
                  status={formatEvidenceStatus(policy.status)}
                  variant={getEvidenceStatusVariant(policy.status)}
                />
              </td>
              <td>
                <button
                  id={`efteruddannelse-insurance-policy-${policy.id}`}
                  type="button"
                  className="btn btn--action"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelectInsurancePolicy(policy.id);
                  }}
                >
                  Vis
                </button>
              </td>
            </tr>
          ))}
        {!isLoading && !error && !policies.length && (
          <tr>
            <td colSpan={7} className="text-muted">
              Ingen forsikringspolicer
            </td>
          </tr>
        )}
      </TableBody>
    </TableRoot>
    <div className="d-flex justify-content-end mt-2">
      <DocumentPaginationControls
        pagination={pagination}
        showPagination={showPagination}
        onPreviousClick={onPreviousPage}
        onNextClick={onNextPage}
      />
    </div>
  </>
);
